import React, { useState, FC, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Portal } from 'react-portal';
import { values, now } from 'lodash';

import * as Types from 'types';

import { getArtworkEditionDescription, getArtworkBundleTitle } from 'artworks/helpers';
import { setActiveArtworkId, fetchArtworkDetailsBySlug } from 'features/artworks/actions';

import { fetchUserById } from 'features/users/actions';

import { ArtworkDetailMediaPreview } from 'artworks/ArtworkDetailMediaPreview';
import { PurchasableBundleBanner } from 'artworks/PurchasableBundleBanner';
import { AddToPlaylistWidget } from 'playlists/AddToPlaylistWidget';
import { ShareModalContent } from 'components/ShareModalContent';
import { Auction } from 'artworks/ArtworkDetail/Auction';
import { SimilarArtworksSlate } from 'artworks/slates/SimilarArtworksSlate';
import { ArtworksByArtistSlate } from 'artworks/slates/ArtworksByArtistSlate';
import { BiddingHistory } from './Auction/BiddingHistory';
import { fetchBids } from 'features/bids/api';

import { ArtworkExtraInfo } from './components/ArtworkExtraInfo';
import { ArtworkCategories } from './components/ArtworkCategories';
import { ConditionalPortal } from './components/ConditionalPortal';
import { ActionButton } from '../../components/UI/ActionButton';
import { SqueezedModal } from 'components/modal/SqueezedModal';
import { DataTable } from 'components/datatable/DataTable';
import { ArtworkActivitySlate } from 'apps/profile/components/ActivitySlate';
import { NftIconWithTooltip } from 'components/icons/NftIcon';

type Props = {
  artworkSlug: string;
};

const SLATE_ITEMS_COUNT = 5;

export const ArtworkDetail: FC<Props> = ({ artworkSlug }) => {
  const dispatch = useDispatch();
  const intervalRef = useRef<number | undefined>();

  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [bids, setBids] = useState<Types.Bid[] | undefined>(undefined);
  const editionId = $('.js-auction-bid').data('edition-id');
  const artworks = useSelector((state: Types.RootState) => values(state.artworks.byId));
  const users = useSelector((state: Types.RootState) => values(state.users.byId));
  const artwork = artworks.find(({ slug }) => slug === artworkSlug);
  const artworkWithDetails = artwork && Types.isArtworkWithDetails(artwork) ? artwork : undefined;

  const artworkWithDetailsId = artworkWithDetails ? artworkWithDetails.id : undefined;
  const artistUserId = artwork?.artistUserId;
  const artistUser = users.find(({ id }) => id === artistUserId);

  // Load artwork
  useEffect(() => {
    if (!artworkWithDetails) {
      dispatch(fetchArtworkDetailsBySlug(artworkSlug));
    }
  }, [artworkSlug]);

  // Load artist
  useEffect(() => {
    if (artistUserId) {
      dispatch(fetchUserById(artistUserId));
    }
  }, [artistUserId]);

  useEffect(() => {
    if (artworkWithDetailsId) {
      dispatch(setActiveArtworkId(artworkWithDetailsId));
    }
  }, [artworkWithDetailsId]);

  const { auction } = artworkWithDetails || {};

  const getBids = async (editionId: number) => {
    const bids = await fetchBids(editionId);
    setBids(bids);
  };

  useEffect(() => {
    if (auction) {
      const auctionHasEnded = auction.endsAt && auction.endsAt < now();
      const auctionIsGoing = auction.startsAt <= now() && !auctionHasEnded;
      if (auctionHasEnded) {
        getBids(editionId);
      } else if (auctionIsGoing) {
        intervalRef.current = window.setInterval(() => {
          getBids(editionId);
        }, 2000);
      }
    }

    return () => {
      clearInterval(intervalRef.current);
    };
  }, [auction]);

  if (!artworkWithDetails) {
    return null;
  }

  const bundleTitle = getArtworkBundleTitle(artworkWithDetails);

  const artistSlug = artworkWithDetails.artistUrl.split('/').slice(-1)[0];

  const columns = React.useMemo(
    () => [
      {
        Header: 'Edition',
        accessor: 'editionNumber',
      },
      {
        Header: 'Owned By',
        accessor: 'ownedBy',
      },
      {
        Header: 'Last Sold',
        accessor: 'lastSold',
      },
      {
        Header: 'Last Sale Time',
        accessor: 'lastSalePrice',
      },
      {
        Header: 'List Price',
        accessor: 'listPrice',
      },
      {
        Header: 'Secondary Market',
        accessor: 'openSeaId',
        Cell: ({ value: { openSeaId } }: any) => (
          <div>
            <a className='link' href='#'>
              <span className='mr-mini'>View on OpenSea</span>
              <span className='icon-open' style={{ fontSize: '10px' }} />
            </a>
          </div>
        ),
      },
    ],
    [],
  );

  return (
    <div>
      {artworkWithDetails.childrenCount === 0 ? (
        <Portal node={$('.js-artwork-detail__preview')[0]}>
          <ArtworkDetailMediaPreview artwork={artworkWithDetails} />
        </Portal>
      ) : null}
      <ConditionalPortal selector='.js-purchasable-description'>
        <div
          className='key-value key-value__value d-flex align-items-center'
          style={{ lineHeight: 1 }}
        >
          {artworkWithDetails.nft && (
            <NftIconWithTooltip style={{ marginTop: '-2px' }} className='mr-1' />
          )}
          {getArtworkEditionDescription(artworkWithDetails.totalEditions, artworkWithDetails.nft)}
          {bundleTitle && <div style={{ marginTop: 3 }}>{bundleTitle}</div>}
        </div>
      </ConditionalPortal>
      <Portal node={$('.js-artwork-detail__categories')[0]}>
        {artworkWithDetails.categories.length && (
          <ArtworkCategories
            categories={artworkWithDetails.categories}
            vip={artworkWithDetails.vip}
          />
        )}
      </Portal>
      <Portal node={$('.js-artwork-detail__extra-info')[0]}>
        <ArtworkExtraInfo
          type={artworkWithDetails.type}
          resolution={artworkWithDetails.resolution}
          remainingEditions={artworkWithDetails.remainingEditions}
          publishedAt={artworkWithDetails.publishedAt}
          duration={artworkWithDetails.duration}
        />
        {bids && bids.length > 0 && <BiddingHistory bids={bids.slice().reverse()} />}
      </Portal>
      {editionId && auction && (
        <Portal node={$('.js-auction-bid')[0]}>
          <Auction
            auction={auction}
            artworkTitle={artworkDetailJSON ? artworkDetailJSON.title : ''}
            editionId={editionId}
            bids={bids}
          />
        </Portal>
      )}
      <Portal node={$('.js-artwork-actions__share')[0]}>
        <ActionButton icon='share' label='Share' handleClick={() => setIsShareModalOpen(true)} />
      </Portal>
      {!artworkWithDetails.soldOut && artworkWithDetails.bundles.length > 0 && (
        <Portal node={$('.js-artwork-actions__before-buy')[0]}>
          <PurchasableBundleBanner bundle={artworkWithDetails.bundles[0]} />
        </Portal>
      )}
      {artworkWithDetails.nft && (
        <ConditionalPortal selector='.js-nft-buying-history'>
          <DataTable
            columns={columns}
            title={'Artwork Details'}
            extraPadding
            hasPagination
            fixedFirstColumn
          />
        </ConditionalPortal>
      )}
      <ConditionalPortal selector='.js-add-to-playlist-widget'>
        <AddToPlaylistWidget artwork={artworkWithDetails} />
      </ConditionalPortal>
      <ConditionalPortal selector='.js-similar-artworks'>
        <>
          <ArtworksByArtistSlate
            artistSlug={artistSlug}
            artistId={artworkWithDetails.artistUserId}
            currentArtworkId={artworkWithDetails.id}
            expectedCount={
              Math.min(artistUser?.artist?.artworksCount || 1, SLATE_ITEMS_COUNT + 1) - 1
            }
            maxCount={5}
          />
          <SimilarArtworksSlate artworkId={artworkWithDetails.id} count={6} />
          <div className='container slate-container'>
            <ArtworkActivitySlate artworkId={artworkWithDetails.id} />
          </div>
        </>
      </ConditionalPortal>
      <SqueezedModal isOpen={isShareModalOpen} onRequestClose={() => setIsShareModalOpen(false)}>
        {artwork && <ShareModalContent url={artwork.url} title={artwork.title} />}
      </SqueezedModal>
    </div>
  );
};
